/**
 * Math utilities and module-level scratch variables.
 * All hot-path math should use these scratch vars instead of allocating new objects.
 */

import * as THREE from 'three/webgpu';

// ─── Scratch Variables (reuse in hot paths, never allocate) ─────────────────────

export const _v1 = new THREE.Vector3();
export const _v2 = new THREE.Vector3();
export const _v3 = new THREE.Vector3();
export const _q1 = new THREE.Quaternion();
export const _q2 = new THREE.Quaternion();
export const _m1 = new THREE.Matrix4();
export const _euler = new THREE.Euler();

// ─── Utility Functions ──────────────────────────────────────────────────────────

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function lerpAngle(a: number, b: number, t: number): number {
  const TWO_PI = Math.PI * 2;
  // Shortest arc difference via modular arithmetic (O(1), no loops)
  const diff = ((b - a) % TWO_PI + TWO_PI + Math.PI) % TWO_PI - Math.PI;
  // Normalize result to [0, 2π) so 360° wraps to 0°
  return ((a + diff * t) % TWO_PI + TWO_PI) % TWO_PI;
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function moveToward(current: number, target: number, maxDelta: number): number {
  if (Math.abs(target - current) <= maxDelta) return target;
  return current + Math.sign(target - current) * maxDelta;
}

export function applyDeadzone(value: number, min: number, max: number): number {
  const abs = Math.abs(value);
  if (abs < min) return 0;
  // >= (was >): identical for max > min ((max-min)/(max-min) === 1) and it keeps min === max off the 0/0 NaN path.
  if (abs >= max) return Math.sign(value);
  return Math.sign(value) * ((abs - min) / (max - min));
}

/**
 * Apply radial deadzone. Writes into the provided `out` object to avoid
 * per-call allocation. Callers must pass their own output object — the
 * result is NOT safe to store across frames without copying.
 */
export function applyRadialDeadzone(
  x: number,
  y: number,
  min: number,
  max: number,
  out: { x: number; y: number } = { x: 0, y: 0 },
): { x: number; y: number } {
  const len = Math.sqrt(x * x + y * y);
  if (len <= min) { out.x = 0; out.y = 0; return out; }
  if (len > max) {
    const inv = 1 / len;
    out.x = x * inv; out.y = y * inv; return out;
  }
  const scale = (len - min) / (max - min) / len;
  out.x = x * scale; out.y = y * scale; return out;
}
